import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import IcomoonReact from 'icomoon-react';
import MuiCheckbox from '@material-ui/core/Checkbox';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Formik } from 'formik';
import { useTheme } from 'emotion-theming';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/components/Text/Text';
import { loadSavedProgram } from '../ducks/calendarActions';
import { showErrorToast } from '../../../shared/utils/toastManager';
import ModalCtaButton from '../components/presentational/ModalCtaButton';
import checkboxTheme from '../../../shared/theme/checkboxTheme';

const CloseButton = styled('button')`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const ModalContent = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  gap: 16px;
  position: relative;
  font-family: 'Nunito Sans';
`;

const Title = styled('h1')`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  max-width: calc(100% - 40px); /* Account for close button */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubText = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: -16px;
`;

const TwoColumnContainer = styled('div')`
  display: flex;
  gap: 24px;
  width: 100%;
`;

const LeftColumn = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const RightColumn = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const DatePickerWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;

  .react-datepicker {
    border: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    font-family: 'Nunito Sans';
    width: 320px;
    background: ${({ theme }) => theme.colors.surfaceElevated};
    color: ${({ theme }) => theme.colors.text};
  }

  .react-datepicker__day-name,
  .react-datepicker__current-month {
    color: ${({ theme }) => theme.colors.text};
  }

  .react-datepicker__day:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  .react-datepicker__header {
    background: ${({ theme }) => theme.colors.surfaceElevated};
    border-bottom: none;
    padding: 15px;
  }

  .react-datepicker__month-container {
    padding: 15px;
    font-family: 'Nunito Sans';
    font-size: 15px;
  }

  .react-datepicker__day-names,
  .react-datepicker__week {
    margin-bottom: 15px;
  }

  /* The library paints every day #000 and has no --inside-month class, so the base
     class carries the text token; --outside-month below dims the spill-over days. */
  .react-datepicker__day {
    color: ${({ theme }) => theme.colors.text};
  }
  .react-datepicker__day--outside-month {
    color: ${({ theme }) => theme.colors.textTertiary};
  }


  .react-datepicker__day--selected {
    background-color: #FF6600;
    color: ${({ theme }) => theme.colors.white};
  }

  .react-datepicker__day--keyboard-selected {
    background: none;
    color: ${({ theme }) => theme.colors.text};
  }

  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle,
  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::before {
    border-bottom-color: ${({ theme }) => theme.colors.surfaceElevated};
  }

  .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle,
  .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle::before {
    border-top-color: ${({ theme }) => theme.colors.surfaceElevated};
  }

  .react-datepicker-popper {
    z-index: 1;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.borderMedium};
  border-radius: 4px;
  padding: 0 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textDark};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  background: ${({ theme }) => theme.colors.surfaceInput};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.disabled ? props.theme.colors.borderMedium : props.theme.colors.textTertiary)};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.textTertiary};
  }
`;

const ActiveDaysText = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 18px;
  font-weight: 600;
  margin: 8px 0;
`;

const DescriptionContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DescriptionDisplay = styled('div')`
  width: 100%;
  min-height: 250px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderMedium};
  border-radius: 4px;
  font-size: 14px;
  background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.5;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  flex: 1;
`;

const CheckboxContainer = styled('div')`
  display: flex;
  gap: 12px;
  flex-direction: column;
`;

const CheckboxWrapper = styled('label')`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

/* Same outlined checkbox Copy Workout draws: a themed SVG box instead of the
   browser's native control, which paints white in dark mode. */
const CustomUncheckedIcon = () => {
  const { colors } = useTheme();
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' stroke={colors.borderLight} />
    </svg>
  );
};

const CustomCheckedIcon = () => {
  const { colors } = useTheme();
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' fill={colors.surface} stroke={colors.hover} />
      <path d='M5 9L8 12L13 6' stroke={colors.hover} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
};

const StyledCheckbox = styled(MuiCheckbox)`
  padding: 4px;

  &:hover {
    background-color: rgba(119, 167, 255, 0.04);
  }
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const EndDateTitleWrapper = styled('div')`
  position: relative;
  display: inline-block;
  cursor: help;
`;

const LoadProgramsModal = ({ closeModal }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedSavedWorkout = useSelector((state) => state.calendar.data.selectedSavedWorkout);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);

  // Helper functions to handle both data structures
  const getWorkoutId = () => selectedSavedWorkout?.info?.id || selectedSavedWorkout?.id;
  const getWorkoutName = () => selectedSavedWorkout?.info?.programName
    || selectedSavedWorkout?.programName;
  const getWorkoutDescription = () => selectedSavedWorkout?.info?.description
    || selectedSavedWorkout?.description;
  const getNumberOfDays = () => selectedSavedWorkout?.info?.numberOfDays
    || selectedSavedWorkout?.numberOfDays;

  const endDateTooltipId = 'end-date-tooltip';

  const initialValues = {
    startDate: selectedDate ? new Date(selectedDate) : new Date(),
    includeTitles: true,
    includeLoads: true,
    replaceWorkouts: false,
  };

  const handleLoadProgram = (values) => {
    if (programType !== 'calendar') {
      // Stub branch — preserved from prior behavior; no dispatch.
      // TODO: Implement endpoint integration for loading non-date-based workouts.
      return;
    }

    if (!getWorkoutId()) {
      showErrorToast('No program selected. Please select a program to load.');
      return;
    }

    if (!selectedReduxCalendar?.id && !selectedReduxAthlete?.id) {
      showErrorToast('Please select a calendar or user to load the program to');
      return;
    }

    const submissionObject = {
      clearExisting: values.replaceWorkouts,
      includePrescribedLoads: values.includeLoads,
      includeWorkoutInfo: values.includeTitles,
      programId: getWorkoutId(),
      startDate: moment(values.startDate).format('YYYY-MM-DD'),
    };

    if (Object.keys(selectedReduxAthlete).length) {
      submissionObject.userId = selectedReduxAthlete.id;
    } else if (Object.keys(selectedReduxCalendar).length) {
      submissionObject.calendarId = selectedReduxCalendar.id;
    }

    dispatch(loadSavedProgram(currentUser.accountCode, submissionObject));
  };

  const { colors } = useTheme();
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleLoadProgram}
      render={({ values, setFieldValue, handleSubmit }) => {
        const numDays = getNumberOfDays() || 0;
        const derivedEndDate = moment(values.startDate).add(numDays, 'days').toDate();
        const startDateAsDate = moment.isMoment(values.startDate)
          ? values.startDate.toDate()
          : values.startDate;

        return (
          <ModalContent>
            <CloseButton onClick={closeModal}>
              <IcomoonReact
                iconSet={iconSet}
                size={14}
                icon='remove'
                color={colors.textSecondary}
              />
            </CloseButton>
            <Title>
              {getWorkoutName()
                ? `Load ${getWorkoutName()}`
                : 'Load Program'}
            </Title>
            <SubText>
              The start and end date for this program have been selected based on the chosen start date
              and the number of total days in the program.
            </SubText>
            <TwoColumnContainer>
              <LeftColumn>
                <DatePickerWrapper>
                  <Text fontWeight={600}>Start Date</Text>
                  <StyledDatePicker
                    selected={startDateAsDate}
                    onChange={(date) => setFieldValue('startDate', date)}
                    placeholderText='Select Date'
                    dateFormat='MMM dd, yyyy'
                  />
                </DatePickerWrapper>

                <ActiveDaysText>
                  Active Days:
                  {' '}
                  {moment(derivedEndDate).diff(moment(startDateAsDate), 'days') + 1}
                </ActiveDaysText>

                <DatePickerWrapper>
                  <EndDateTitleWrapper>
                    <div
                      style={{ display: 'inline-block' }}
                      data-tooltip-id={endDateTooltipId}
                    >
                      <Text fontWeight={600}>Program to End On:</Text>
                    </div>
                  </EndDateTitleWrapper>
                  <StyledDatePicker
                    selected={derivedEndDate}
                    placeholderText='Select Date'
                    dateFormat='MMM dd, yyyy'
                    disabled
                  />
                </DatePickerWrapper>

                <CheckboxContainer>
                  <CheckboxWrapper>
                    <MuiThemeProvider theme={checkboxTheme}>
                      <StyledCheckbox
                        checked={values.includeTitles}
                        onChange={(e) => setFieldValue('includeTitles', e.target.checked)}
                        icon={<CustomUncheckedIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        color='default'
                      />
                    </MuiThemeProvider>
                    <Text>Include workout titles and colors</Text>
                  </CheckboxWrapper>
                  <CheckboxWrapper>
                    <MuiThemeProvider theme={checkboxTheme}>
                      <StyledCheckbox
                        checked={values.includeLoads}
                        onChange={(e) => setFieldValue('includeLoads', e.target.checked)}
                        icon={<CustomUncheckedIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        color='default'
                      />
                    </MuiThemeProvider>
                    <Text>Include prescribed loads</Text>
                  </CheckboxWrapper>
                  <CheckboxWrapper>
                    <MuiThemeProvider theme={checkboxTheme}>
                      <StyledCheckbox
                        checked={values.replaceWorkouts}
                        onChange={(e) => setFieldValue('replaceWorkouts', e.target.checked)}
                        icon={<CustomUncheckedIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        color='default'
                      />
                    </MuiThemeProvider>
                    <Text>Replace workouts for all selected dates</Text>
                  </CheckboxWrapper>
                </CheckboxContainer>
              </LeftColumn>

              <RightColumn>
                <DescriptionContainer>
                  <Text fontWeight={600}>Description</Text>
                  <DescriptionDisplay>
                    {getWorkoutDescription() || ''}
                  </DescriptionDisplay>
                </DescriptionContainer>
              </RightColumn>
            </TwoColumnContainer>

            <ButtonContainer>
              <ModalCtaButton type='button' primary onClick={() => handleSubmit()}>Load Program</ModalCtaButton>
              <ModalCtaButton type='button' onClick={closeModal}>CANCEL</ModalCtaButton>
            </ButtonContainer>

            {/* ReactTooltip component */}
            <ReactTooltip
              id={endDateTooltipId}
              place='top'
              content='End date is based on start date and active days'
              style={{
                backgroundColor: '#A6A6A6',
                color: colors.white,
                fontFamily: 'Nunito Sans',
                fontSize: '13px',
              }}
            />
          </ModalContent>
        );
      }}
    />
  );
};

LoadProgramsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default LoadProgramsModal;
